import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ReceivedGift {
  id: string;
  amount: number;
  net_amount: number;
  message: string;
  status: string;
  created_at: string;
  currency: string;
  is_anonymous: boolean;
  purpose: string | null;
  userName?: string;
  sender_id?: string;
  sender: {
    name: string;
    avatar_url: string;
  } | null;
}

export const useReceivedGifts = (userId?: string | null) => {
  return useQuery({
    queryKey: ["received-gifts", userId],
    queryFn: async () => {
      if (!userId) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("gifts")
        .select(`
          id,
          amount,
          net_amount,
          message,
          status,
          created_at,
          currency,
          is_anonymous,
          purpose,
          sender_id
        `)
        .eq("recipient_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      console.log('[useReceivedGifts] raw rows', data);

      const rows = (data || []) as Array<any>;
      const senderIds = Array.from(
        new Set(
          rows
            .filter((g) => !g.is_anonymous && g.sender_id)
            .map((g) => g.sender_id as string)
        )
      );

      let profiles: Record<string, { name: string; avatar_url: string }> = {};
      if (senderIds.length) {
        const { data: usersData, error: usersError } = await (supabase as any)
          .from('public_profiles')
          .select('id, name, username, avatar_url')
          .in('id', senderIds);
        if (usersError) throw usersError;
        for (const u of (usersData || []) as any[]) {
          const displayName = (u.name && u.name.trim()) || (u.username && u.username.trim()) || 'Unknown';
          profiles[u.id as string] = { name: displayName, avatar_url: u.avatar_url || '' };
        }
      }

      const result: ReceivedGift[] = rows.map((g) => ({
        id: g.id,
        amount: g.amount,
        net_amount: g.net_amount,
        message: g.message,
        status: g.status,
        created_at: g.created_at,
        currency: g.currency,
        is_anonymous: g.is_anonymous,
        purpose: g.purpose,
        sender_id: g.sender_id,
        sender: g.is_anonymous ? null : (profiles[g.sender_id] || null),
      }));
      console.log('[useReceivedGifts] mapped', result);

      return result;
    },
    enabled: !!userId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });
};
